import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, AlertTriangle, Shield, Users, Wrench, Building2, Layers, MapPin,
  TrendingUp, Calendar, ArrowRight, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADO_COLORS = {
  EN_VALIDACION: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  PEND_APROBACION: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  APROBADA: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  RECHAZADA: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
};
const ESTADO_LABELS = { EN_VALIDACION: 'En Validacion', PEND_APROBACION: 'Pend. Aprobacion', APROBADA: 'Aprobada', RECHAZADA: 'Rechazada' };
const RIESGO_COLORS = { BAJO: 'bg-green-500', MEDIO: 'bg-yellow-500', ALTO: 'bg-orange-500', CRITICO: 'bg-red-500' };

function StatCard({ icon: Icon, label, value, sub, color = '#1B919B', onClick }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function BarChart({ data, color = '#1B919B', maxItems = 6 }) {
  const items = data.slice(0, maxItems);
  const max = Math.max(...items.map(d => d.total), 1);
  return (
    <div className="space-y-2">
      {items.map((d, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-slate-600 w-[140px] truncate text-right">{d.nombre}</span>
          <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(d.total / max) * 100}%`, backgroundColor: color }} />
          </div>
          <span className="text-xs font-bold text-slate-700 w-8 text-right">{d.total}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardPlanificador() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/dashboard-planificador`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [token]);

  if (loading) return <div className="py-20 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B919B] mx-auto" /></div>;
  if (!data) return <div className="py-20 text-center text-slate-400">Error al cargar datos</div>;

  const { por_estado, por_gerencia, por_area, por_riesgo, por_sector, top_empresas, top_riesgos_actividades, proximas_actividades } = data;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="dashboard-planificador">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={ClipboardList} label="Total Actividades" value={data.total_actividades} sub={`${data.actividades_semana} esta semana`} color="#1B919B"
          onClick={() => navigate('/planificador/actividades')} />
        <StatCard icon={Activity} label="Aprobadas" value={por_estado.APROBADA || 0} sub={`${por_estado.PEND_APROBACION || 0} pendientes`} color="#10b981" />
        <StatCard icon={AlertTriangle} label="Riesgos Catalogo" value={data.total_riesgos_catalogo} sub={`${data.total_controles_catalogo} controles`} color="#f97316"
          onClick={() => navigate('/planificador/riesgos')} />
        <StatCard icon={Users} label="Personal" value={data.total_personal_asignado} sub="asignado a actividades" color="#6366f1"
          onClick={() => navigate('/planificador/personas')} />
        <StatCard icon={Wrench} label="Recursos" value={data.total_recursos_asignados} sub="asignados" color="#a05e49"
          onClick={() => navigate('/planificador/recursos')} />
        <StatCard icon={Building2} label="Empresas" value={top_empresas.length} sub="contratistas activas" color="#8b5cf6" />
      </div>

      {/* Estado distribution */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(ESTADO_LABELS).map(([key, label]) => {
          const c = ESTADO_COLORS[key];
          const count = por_estado[key] || 0;
          const pct = data.total_actividades > 0 ? Math.round((count / data.total_actividades) * 100) : 0;
          return (
            <div key={key} className={`${c.bg} ${c.border} border rounded-xl p-3`}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                <span className={`text-xs font-medium ${c.text}`}>{label}</span>
              </div>
              <p className="text-xl font-bold text-slate-800">{count}</p>
              <div className="w-full h-1.5 bg-white/60 rounded-full mt-1.5">
                <div className={`h-full rounded-full ${c.dot}`} style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">{pct}% del total</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Gerencia */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-[#1B919B]" />
            <h3 className="text-sm font-semibold text-slate-700">Actividades por Gerencia</h3>
          </div>
          {por_gerencia.length > 0 ? <BarChart data={por_gerencia} color="#1B919B" /> : <p className="text-xs text-slate-400">Sin datos</p>}
        </div>

        {/* By Area */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 text-[#a05e49]" />
            <h3 className="text-sm font-semibold text-slate-700">Actividades por Area</h3>
          </div>
          {por_area.length > 0 ? <BarChart data={por_area} color="#a05e49" /> : <p className="text-xs text-slate-400">Sin datos</p>}
        </div>

        {/* Top Empresas */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 text-[#8b5cf6]" />
            <h3 className="text-sm font-semibold text-slate-700">Top Empresas Contratistas</h3>
          </div>
          {top_empresas.length > 0 ? <BarChart data={top_empresas} color="#8b5cf6" /> : <p className="text-xs text-slate-400">Sin empresas con actividades</p>}
        </div>

        {/* By Sector */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4 text-[#f59e0b]" />
            <h3 className="text-sm font-semibold text-slate-700">Actividades por Sector</h3>
          </div>
          {por_sector.length > 0 ? <BarChart data={por_sector} color="#f59e0b" /> : <p className="text-xs text-slate-400">Sin datos</p>}
        </div>
      </div>

      {/* Riesgo distribution + Top Riesgos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Risk level donut-like */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            <h3 className="text-sm font-semibold text-slate-700">Nivel de Riesgo</h3>
          </div>
          <div className="space-y-3">
            {['CRITICO', 'ALTO', 'MEDIO', 'BAJO'].map(level => {
              const count = por_riesgo[level] || 0;
              const pct = data.total_actividades > 0 ? Math.round((count / data.total_actividades) * 100) : 0;
              return (
                <div key={level} className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white w-16 text-center ${RIESGO_COLORS[level]}`}>{level}</span>
                  <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${RIESGO_COLORS[level]}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Riesgos in activities */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-[#1B919B]" />
            <h3 className="text-sm font-semibold text-slate-700">Riesgos Mas Frecuentes en Actividades</h3>
          </div>
          {top_riesgos_actividades.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {top_riesgos_actividades.map((r, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0 ${RIESGO_COLORS[r.nivel] || 'bg-slate-400'}`}>{r.nivel}</span>
                  <span className="text-xs text-slate-700 truncate flex-1">{r.descripcion}</span>
                  <span className="text-xs font-bold text-slate-500 flex-shrink-0">{r.total}x</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400">Sin riesgos registrados</p>}
        </div>
      </div>

      {/* Upcoming Activities */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1B919B]" />
            <h3 className="text-sm font-semibold text-slate-700">Proximas Actividades</h3>
          </div>
          <button onClick={() => navigate('/planificador/actividades')} className="text-xs text-[#1B919B] hover:underline flex items-center gap-1">
            Ver todas <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {proximas_actividades.length > 0 ? (
          <div className="space-y-2">
            {proximas_actividades.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => navigate('/planificador/actividades')} data-testid={`upcoming-${a.codigo}`}>
                <div className="w-10 text-center">
                  <p className="text-lg font-bold text-slate-800 leading-none">{a.fecha.split('-')[2]}</p>
                  <p className="text-[9px] text-slate-400 uppercase">
                    {new Date(a.fecha + 'T12:00:00').toLocaleDateString('es-CL', { month: 'short' })}
                  </p>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{a.titulo}</p>
                  <p className="text-[10px] text-slate-400">{a.area}{a.sector ? ` / ${a.sector}` : ''}</p>
                </div>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded text-white ${RIESGO_COLORS[a.riesgo] || 'bg-slate-400'}`}>{a.riesgo}</span>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                  a.estado === 'APROBADA' ? 'bg-emerald-100 text-emerald-700' :
                  a.estado === 'PEND_APROBACION' ? 'bg-purple-100 text-purple-700' :
                  a.estado === 'EN_VALIDACION' ? 'bg-blue-100 text-blue-700' :
                  'bg-red-100 text-red-700'
                }`}>{ESTADO_LABELS[a.estado] || a.estado}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-slate-400">No hay actividades proximas</p>}
      </div>
    </div>
  );
}
